import React, { useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import { api } from "aldoo-ra/API"
import { store } from "aldoo-ra/Store"
import Localization from "aldoo-ra/Localization"
import Pricing from "@components/Pricing"

const StoreAPI = api("store")

/**
 * Deep-link into the purchase flow: /buy?product=<store product id>
 * Resolves the product, adds it to the cart and opens the cart dialog on
 * top of the pricing page. Used by external landing pages (e.g.
 * /lp-android-1) whose CTAs can't call the store directly.
 */
const BuyPage = (props) => {
  const { lt } = Localization()
  const firedRef = useRef(false)
  // Pricing mounts only after the cart dialog is up — otherwise (especially
  // on mobile, where the cart is a modal) the visitor watches the pricing
  // page load behind it. Until then a clean canvas backdrop shows.
  const [showBackground, setShowBackground] = React.useState(false)

  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true

    const params = new URLSearchParams(window.location.search)
    // Post-add URL: keep utm/campaign params (GA session attribution)
    // and drop only the product so refresh does not re-add
    const pricingUrl = () => {
      const kept = new URLSearchParams(window.location.search)
      kept.delete("product")
      const qs = kept.toString()
      return qs ? `/pricing?${qs}` : "/pricing"
    }
    const productId = params.get("product")

    // Remember where this purchase intent came from (landing page / campaign)
    // — the Store attaches it to the GA4 purchase event
    const origin = params.get("utm_source") || params.get("from")
    if (origin) sessionStorage.setItem("aldoo-purchase-origin", origin)

    if (!productId) {
      window.history.replaceState({}, "", pricingUrl())
      setShowBackground(true)
      return
    }

    ;(async () => {
      try {
        const result = await StoreAPI({
          resolve_price: { products: [productId], platform: "Web" },
        })
        const product = result?.find?.((p) => p.product_id === productId)
        if (!product) {
          window.history.replaceState({}, "", pricingUrl())
          setShowBackground(true)
          return
        }

        product.name && (product.name = await lt(product.name))
        product.description &&
          (product.description = await lt(product.description))

        PubSub.publish("Tracking_ViewItem", {
          currency: product.currency,
          value: Number(product.amount) || 0,
          items: [
            {
              item_id: product.product_id,
              item_name: product.name || product.product_id,
              item_list_name: "BuyDeepLink",
              price: Number(product.amount) || 0,
              quantity: 1,
            },
          ],
        })

        store.addToCart({ product, proceedToCart: true })

        // Clean URL so a refresh lands on pricing instead of re-adding
        window.history.replaceState({}, "", pricingUrl())
        // Cart dialog is visible now — load the pricing page behind it
        setShowBackground(true)
      } catch (error) {
        console.error("BuyPage: failed to resolve product", error)
        window.history.replaceState({}, "", pricingUrl())
        setShowBackground(true)
      }
    })()
  }, [])

  // Pricing renders hidden behind the open cart dialog, ready for when the
  // visitor closes it; before that, just the site canvas
  if (!showBackground) return <div className="min-h-screen bg-canvas" />
  return <Pricing {...props} />
}

export default BuyPage
